/**
 * What Rescore tells the drafter about how the score moved.
 *
 * Rescore re-reads the documents and re-runs the whole analysis, summary
 * included, so the score can move a few points with no document change
 * (CS-13: 79, then 78, then 85). Saying by how much, and why, keeps the
 * movement from reading as a bug.
 */

/** The Completeness panel's labels, in its order. Prior art is scored
 *  but not shown there and does not count towards the overall, so it is
 *  left out here too. */
export const SCORE_PARAM_LABELS: Record<string, string> = {
  problem: 'Problem',
  solution: 'Solution',
  technical_effect: 'Tech Effect',
  embodiments: 'Embodiments',
  advantages: 'Advantages',
};

export interface ScoreSnapshot {
  overall: number;
  scores: Record<string, number>;
}

export interface ScoreChange {
  param: string;
  from: number;
  to: number;
  delta: number;
}

export const RESCORE_REASON =
  'Rescore re-reads the documents and re-runs the analysis, so scores can '
  + 'move a few points even when nothing changed.';

// What the panel shows: the value rounded, a missing one as 0.
const shown = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0;

// A real minus sign, so "−3" does not read as a hyphen.
const signed = (n: number): string => (n > 0 ? `+${n}` : `−${Math.abs(n)}`);

/** Whether the panel would show a different number anywhere. */
export function scoresMoved(before: ScoreSnapshot, after: ScoreSnapshot): boolean {
  return shown(before.overall) !== shown(after.overall)
    || Object.keys(SCORE_PARAM_LABELS).some(
      (param) => shown(before.scores?.[param]) !== shown(after.scores?.[param]),
    );
}

/** The notice text and the parameters that moved, biggest move first.
 *  Null when there was no score before (a first Analyze). */
export function describeRescoreDelta(
  before: ScoreSnapshot | null | undefined,
  after: ScoreSnapshot,
): { text: string; changed: ScoreChange[] } | null {
  if (!before) return null;
  const changed = Object.keys(SCORE_PARAM_LABELS)
    .map((param) => {
      const from = shown(before.scores?.[param]);
      const to = shown(after.scores?.[param]);
      return { param, from, to, delta: to - from };
    })
    .filter((c) => c.delta !== 0)
    // sort is stable, so equal moves keep the panel's order.
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const from = shown(before.overall);
  const to = shown(after.overall);
  const parts = [`Rescore: ${from} → ${to} (${to === from ? 'no change' : signed(to - from)}).`];
  if (changed.length > 0) {
    parts.push(`${changed.map((c) => `${SCORE_PARAM_LABELS[c.param]} ${signed(c.delta)}`).join(', ')}.`);
  }
  if (changed.length > 0 || to !== from) parts.push(RESCORE_REASON);
  return { text: parts.join(' '), changed };
}
